/*
  Template tests on product pages, run as Intelligems content tests (TASKS
  #197, removal marker pdp-template-test). Loaded on every product page from
  layout/theme.liquid; it does nothing until a test group names a template.

  HOW A TEST IS SET UP
  The variant is an alternate product template made in the customizer on the
  live theme (product.<suffix>.json, a duplicate of the product's template). In
  Intelligems it is a content test, not a Template Change. The variant group
  runs one line of custom JavaScript, mode Immediately, naming the ORIGINAL
  product's handle and the variant template's suffix:
    document.documentElement.setAttribute('data-pdp-view-<handle>', '<suffix>');
  The control group runs nothing. An Audience rule (not page targeting) decides
  who is placed, so the attribute is on every page a placed shopper opens and
  the family switcher can load the variant from any of them.

  Intelligems' own Template Change reloads the page with ?view= on every visit
  in a variant group, after the page has painted, and never sees the family
  switcher, which loads products with fetch(). This file does both jobs without
  a reload:
  - Direct visit: the variant may differ from the product's template only
    BELOW the main (buy box) section. Once Intelligems has run, the page
    fetches itself with ?view=<suffix> and swaps in the sections after the
    main section. The main section and anything above it stay as they are, so
    nothing above the fold repaints. On a slow phone's first visit Intelligems
    runs 6-8 s in, so the swap comes after that.
  - Family switcher: registers window.JiYuProductViewResolver, which
    assets/product-family-selector.js asks before it fetches a product.

  SAFETY
  - Shopify answers ?view= for a template the theme doesn't have with HTTP 200
    and the base product.json template. Both paths check the suffix this file's
    own script tag carries in the fetched page and ignore any other template.
    So on the draft, where the variant template doesn't exist (customizer
    files never deploy from git), every shopper stays on control.
  - Intelligems' price-test copies (TASKS #133) render the original's
    template; on a copy's page the attribute is found through the copy's
    original.
  - ?view= landing pages and the theme editor are left alone.

  QA: an Intelligems preview link (?ig-preview=<experience id>&igTg=<group
  id>) runs the group's JavaScript. Reload after switching groups in the
  preview: the attribute stays on <html> until the page reloads. Without
  Intelligems, set the attribute in the console: the switcher reads it on
  the next switch to a product it hasn't switched to yet on that page.
*/
(function () {
  if (window.JiyuPdpTemplateTest) return;

  var script = document.currentScript;
  var PAGE_HANDLE = (script && script.getAttribute('data-product-handle')) || '';
  var PAGE_SUFFIX = (script && script.getAttribute('data-template-suffix')) || '';
  var PREFIX = 'data-pdp-view-';
  /* How long the switcher's question waits for Intelligems before this switch
     goes ahead on the product's own template (the switcher caps it lower). */
  var RESOLVE_WAIT_MS = 3000;

  var root = document.documentElement;

  /* { original handle: template suffix } from <html>'s data-pdp-view-*. */
  function views() {
    var out = {};
    var attrs = root.attributes;
    for (var i = 0; i < attrs.length; i++) {
      var name = attrs[i].name;
      var value = (attrs[i].value || '').trim();
      if (name.indexOf(PREFIX) === 0 && value) out[name.slice(PREFIX.length)] = value;
    }
    return out;
  }

  /* Intelligems has run its custom JavaScript, stopped (a bot), or its script
     failed to load (noted by snippets/intelligems.liquid). Same test as the
     gallery gate in layout/theme.liquid. */
  function igDone() {
    if (window.jiyuIntelligemsFailed) return true;
    try {
      return ['ig:loaded', 'ig:ready', 'ig:shutdown'].some(function (name) {
        return performance.getEntriesByName(name).length > 0;
      });
    } catch (e) {
      return false;
    }
  }

  /* Calls every listener whenever Intelligems may have decided something. */
  var listeners = [];
  var watching = false;
  function onIntelligems(fn) {
    listeners.push(fn);
    if (watching) return;
    watching = true;
    var fire = function () {
      setTimeout(function () {
        listeners.slice().forEach(function (listener) { listener(); });
      }, 0);
    };
    window.addEventListener('ig:ready', fire);
    /* An assignment made after start-up runs its custom JavaScript then. */
    window.addEventListener('ig:variationAssignments', fire);
    window.addEventListener('jiyu:intelligems-failed', fire);
    var ig = window.JiyuIntelligems;
    if (ig && ig.READY_EVENT) document.addEventListener(ig.READY_EVENT, fire);
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.name === 'ig:loaded' || entry.name === 'ig:shutdown') fire();
        });
      }).observe({ entryTypes: ['mark'] });
    } catch (e) {}
  }

  /* The template suffix a fetched page was rendered with, read from this
     file's script tag in it, or null when the tag is missing. */
  function renderedSuffix(doc) {
    var tag = doc.querySelector('script[data-pdp-template-test]');
    return tag ? tag.getAttribute('data-template-suffix') || '' : null;
  }

  /* ---------------------------------------------------------------------
     Family switcher. Called with the ORIGINAL handle of the product being
     switched to, also for a shopper on price-test copies. The switcher
     memoizes a string answer for the page, so an answer is given only once
     Intelligems has run; until then the promise rejects at the timeout and
     that switch goes ahead on the product's own template, uncached. Chains to
     any resolver registered before this one.
     --------------------------------------------------------------------- */
  var previousResolver = window.JiYuProductViewResolver;
  function delegate(handle) {
    return typeof previousResolver === 'function' ? previousResolver(handle) : '';
  }

  window.JiYuProductViewResolver = function (handle) {
    if (igDone()) return views()[handle] || delegate(handle);
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        reject(new Error('Intelligems has not run yet'));
      }, RESOLVE_WAIT_MS);
      onIntelligems(function () {
        if (done || !igDone()) return;
        done = true;
        clearTimeout(timer);
        resolve(views()[handle] || delegate(handle));
      });
    });
  };

  /* ---------------------------------------------------------------------
     Direct visit.
     --------------------------------------------------------------------- */
  var content = document.getElementById('MainContent');
  var startMain = content && content.querySelector(':scope > .shopify-section[id$="__main"]');
  var startPath = window.location.pathname;
  var started = false;
  var domReady = document.readyState === 'complete';
  if (!domReady) {
    document.addEventListener('DOMContentLoaded', function () { domReady = true; }, { once: true });
  }

  /* The suffix named for this page's product: by its own handle, or on a
     price-test copy by the original whose copy this is. */
  function pageView() {
    var all = views();
    if (all[PAGE_HANDLE]) return all[PAGE_HANDLE];
    var ig = window.JiyuIntelligems;
    if (!ig || typeof ig.isReady !== 'function' || !ig.isReady()) return '';
    for (var handle in all) {
      if (!Object.prototype.hasOwnProperty.call(all, handle)) continue;
      var product = ig.productFor(handle);
      if (product && product.handle === PAGE_HANDLE) return all[handle];
    }
    return '';
  }

  /* Replaces the sections after the live main section with the ones after the
     main section in the fetched page. */
  function swap(html, view) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    if (renderedSuffix(doc) !== view) return;
    /* The shopper switched product while the page was on its way. */
    if (window.location.pathname !== startPath || !startMain.isConnected) return;
    var current = startMain.parentNode;
    var incoming = doc.getElementById('MainContent');
    if (!current || current.id !== 'MainContent' || !incoming) return;

    var sections = Array.prototype.slice.call(incoming.querySelectorAll(':scope > .shopify-section'));
    var mainIndex = -1;
    for (var i = 0; i < sections.length; i++) {
      if (/__main$/.test(sections[i].id)) {
        mainIndex = i;
        break;
      }
    }
    if (mainIndex === -1) return;

    var fragment = document.createDocumentFragment();
    var added = sections.slice(mainIndex + 1).map(function (section) {
      var adopted = document.adoptNode(section);
      adopted.querySelectorAll('.scroll-trigger').forEach(function (el) {
        el.classList.add('scroll-trigger--cancel');
      });
      fragment.appendChild(adopted);
      return adopted;
    });

    var old = [];
    for (var node = startMain.nextElementSibling; node; node = node.nextElementSibling) {
      if (node.classList.contains('shopify-section')) old.push(node);
    }
    old.forEach(function (section) { section.remove(); });
    current.insertBefore(fragment, startMain.nextSibling);

    /* Injected sections arrive inert: load their missing assets, re-run their
       inline scripts, send shopify:section:load, re-init the review widgets.
       product-family-selector.js (loaded by main-product on every PDP)
       provides this. */
    if (typeof window.JiYuHydrateSections === 'function') {
      try { window.JiYuHydrateSections(current, added); } catch (e) {}
    }
    try { performance.mark('pdp-template-test:swapped'); } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('jiyu:pdp-template-test:swapped', { detail: { view: view } }));
    } catch (e) {}
  }

  function tryDirectVisit() {
    if (started || !igDone()) return;
    var view = pageView();
    if (!view || view === PAGE_SUFFIX) return;
    started = true;
    var url = new URL(window.location.href);
    url.searchParams.set('view', view);
    fetch(url.pathname + url.search, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : null; })
      .then(function (html) {
        if (!html) return;
        if (domReady) {
          swap(html, view);
        } else {
          document.addEventListener('DOMContentLoaded', function () { swap(html, view); }, { once: true });
        }
      })
      .catch(function () {});
  }

  var directVisit =
    PAGE_HANDLE &&
    startMain &&
    !(window.Shopify && window.Shopify.designMode) &&
    !new URLSearchParams(window.location.search).get('view');
  if (directVisit) {
    tryDirectVisit();
    onIntelligems(tryDirectVisit);
  }

  window.JiyuPdpTemplateTest = {
    /* { handle: suffix } named by the shopper's test groups. */
    views: views,
    renderedSuffix: renderedSuffix
  };
})();
